import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import {
  ZoomIn, ZoomOut, FileText, X, ChevronLeft, ChevronRight,
  Search, Download, Keyboard,
  Moon, Sun, List, Trash2, RotateCw, LayoutGrid, Printer,
  BookOpen, Bookmark
} from 'lucide-react';
import PrecacheButtonWithBadge from '../PrecacheButtonWithBadge';

/**
 * Formatea una fecha ISO a "DD/MM/YYYY". Devuelve null si no es válida.
 */
const formatFechaCorta = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch {
    return null;
  }
};

/**
 * Convierte una cantidad de días a un texto humano amistoso para mostrar
 * la antigüedad de la última revisión del documento.
 *  - < 30 días  → "hace N días"
 *  - 30-364     → "hace N meses"
 *  - 365-729    → "hace 1 año"
 *  - >= 730     → "hace N años"
 * Devuelve null si el input no es válido o es negativo.
 */
const formatAntiguedad = (dias) => {
  if (typeof dias !== 'number' || isNaN(dias) || dias < 0) return null;
  if (dias < 30) {
    return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`;
  }
  if (dias < 365) {
    const meses = Math.floor(dias / 30);
    return `hace ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
  }
  const anios = Math.floor(dias / 365);
  return `hace ${anios} ${anios === 1 ? 'año' : 'años'}`;
};

const PDFToolbar = ({
  isFullscreen,
  displayName, pageNumber, numPages, numeroReferencia,
  goToPage, scale, isUpgradingQuality,
  handleZoomIn, handleZoomOut, handleZoomReset,
  showSearchPanel, setShowSearchPanel,
  showTableOfContents, setShowTableOfContents,
  showThumbnails, setShowThumbnails,
  showKeyboardHelp, setShowKeyboardHelp,
  nightMode, setNightMode,
  rotation, handleRotate,
  canPrint, setShowPrintDialog,
  isSuperAdmin, isAdmin, handleDownload,
  clearingCache, handleClearHDCache,
  documentId,
  alreadyRendered = false,
  pdfSizeBytes = 0,
  cachedPages, tableOfContents, tocLoading,
  onClose,
  continuousMode, setContinuousMode,
  // Marcadores (bookmarks)
  showBookmarksPanel, setShowBookmarksPanel,
  marcadoresCount,
  // Marquesina rotativa (revisión + usuarios únicos)
  documentStats,
  // Ref del container de scroll — usado para scrollear en modo continuo sin tocar goToPage del motor.
  containerRef,
  // Helper de navegación pasado por el padre (unificado entre toolbar + footer mobile).
  // Si no viene, lo derivamos localmente como fallback.
  navegarAPagina: navegarAPaginaProp,
  // Ref para medición dinámica de altura desde el padre
  innerRef,
}) => {
  // ============================
  // Marquesina rotativa
  // ============================
  // Armamos las "frases" que vamos a rotar abajo del título del documento.
  // Reglas (acordadas con el usuario):
  //  - Siempre mostrar la revisión + fecha (corta) si existe.
  //  - Sumar "X talleres lo abrieron" SOLO si usuarios_unicos > 0.
  //  - Tick 1 (8s): "Ref: XXX · N usuarios lo abrieron" (Ref siempre adelante; los usuarios solo si > 0)
  //  - Tick 2 (8s): "Rev. Y · DD/MM/YYYY · hace N años" (solo si hay datos de revisión)
  //  - Si no hay stats / docId aún, fallback al modo tradicional "Ref: XXX".
  const fechaRevisionCorta = formatFechaCorta(documentStats?.fecha_revision);
  const antiguedadHumana = formatAntiguedad(documentStats?.dias_desde_revision);
  const tieneUsuarios = !!(documentStats?.usuarios_unicos && documentStats.usuarios_unicos > 0);
  const tieneRevision = !!(documentStats?.revision || fechaRevisionCorta);

  // Tick 1 — Ref + usuarios (cuando aplica)
  const tickRefUsuarios = (() => {
    if (!numeroReferencia && !tieneUsuarios) return null;
    const parts = [];
    if (numeroReferencia) parts.push(`Ref: ${numeroReferencia}`);
    if (tieneUsuarios) {
      parts.push(
        `${documentStats.usuarios_unicos} ${documentStats.usuarios_unicos === 1 ? 'usuario lo abrió' : 'usuarios lo abrieron'}`
      );
    }
    return parts.join(' · ');
  })();

  // Tick 2 — Revisión + fecha + antigüedad
  const tickRevision = (() => {
    if (!tieneRevision) return null;
    const parts = [];
    if (documentStats?.revision) parts.push(`Rev. ${documentStats.revision}`);
    if (fechaRevisionCorta) parts.push(fechaRevisionCorta);
    if (antiguedadHumana) parts.push(antiguedadHumana);
    return parts.join(' · ');
  })();

  const marquesinaItems = [tickRefUsuarios, tickRevision].filter(Boolean);
  const [marquesinaIdx, setMarquesinaIdx] = useState(0);
  useEffect(() => {
    if (marquesinaItems.length < 2) return;
    const t = setInterval(() => {
      setMarquesinaIdx((i) => (i + 1) % marquesinaItems.length);
    }, 8000);
    return () => clearInterval(t);
  }, [marquesinaItems.length]);
  const marquesinaActual = marquesinaItems.length
    ? marquesinaItems[marquesinaIdx % marquesinaItems.length]
    : null;

  // Handler unificado para navegar a una página desde la toolbar.
  //  - Modo página individual: usa goToPage (motor del visor).
  //  - Modo continuo: hace scrollIntoView a `[data-page-num="N"]` (mismo patrón seguro
  //    que PDFTableOfContents y PDFThumbnails — NO toca el goToPage del motor para
  //    evitar conflictos con el IntersectionObserver de PDFContinuousView).
  // Helper de navegación: usa el pasado por padre si está, o uno local como fallback.
  const navegarAPagina = navegarAPaginaProp || ((pg) => {
    const n = parseInt(pg, 10);
    if (!n || n < 1 || n > numPages) return;
    if (continuousMode && containerRef?.current) {
      const targetEl = containerRef.current.querySelector(`[data-page-num="${n}"]`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    goToPage(n);
  });

  // Estado local del input de página: permite tipear varios dígitos sin que cada
  // tecla dispare la navegación. Sincroniza con pageNumber cuando cambia desde fuera.
  const [pageInputValue, setPageInputValue] = useState(String(pageNumber));
  useEffect(() => {
    setPageInputValue(String(pageNumber));
  }, [pageNumber]);

  // Commit de la navegación: al hacer Enter o blur (perder foco) en el input.
  const commitPageInput = () => {
    const n = parseInt(pageInputValue, 10);
    if (!n || n < 1 || n > numPages) {
      setPageInputValue(String(pageNumber));
      return;
    }
    if (n === pageNumber) return;
    navegarAPagina(n);
  };

  // Auto-commit: si el usuario tipea y se queda 3s sin interactuar, navegar igual
  // (algunos usuarios esperan que el visor se mueva solo sin tocar Enter ni hacer click afuera).
  const autoCommitTimerRef = useRef(null);
  useEffect(() => {
    // Limpiar timer anterior cada vez que el input cambia.
    if (autoCommitTimerRef.current) {
      clearTimeout(autoCommitTimerRef.current);
      autoCommitTimerRef.current = null;
    }
    // No disparar si el valor ya coincide con pageNumber (no hay nada que cambiar).
    if (pageInputValue === String(pageNumber)) return;
    autoCommitTimerRef.current = setTimeout(() => {
      commitPageInput();
    }, 3000);
    return () => {
      if (autoCommitTimerRef.current) {
        clearTimeout(autoCommitTimerRef.current);
        autoCommitTimerRef.current = null;
      }
    };
  }, [pageInputValue, pageNumber]);

  // Texto del subtítulo: marquesina > Ref (fallback puro) > Página
  let subtitulo;
  let subtituloTooltip;
  if (marquesinaActual) {
    subtitulo = marquesinaActual;
    subtituloTooltip = marquesinaActual;
  } else if (numeroReferencia) {
    subtitulo = `Ref: ${numeroReferencia}`;
    subtituloTooltip = numeroReferencia;
  } else {
    subtitulo = `Pagina ${pageNumber} de ${numPages}`;
    subtituloTooltip = subtitulo;
  }

  return (
    <div
      ref={innerRef}
      className={`flex items-center justify-between gap-3 px-4 py-1.5 bg-slate-800 border-b border-slate-700 flex-wrap ${isFullscreen ? 'flex-shrink-0' : ''}`}
    >
      {/* Info del documento — mobile: ancho completo (los controles caen abajo en 1 fila horizontal).
          Desktop: ancho acotado lg:max-w-[280px] xl:max-w-[360px]. */}
      <div className="flex items-center gap-2 min-w-0 w-full md:w-auto md:flex-shrink lg:max-w-[280px] xl:max-w-[360px]">
        <div className="p-2 bg-teal-500/20 rounded-lg flex-shrink-0">
          <FileText className="h-5 w-5 text-teal-400" />
        </div>
        <div className="min-w-0">
          <h3 className="text-white font-semibold truncate text-sm md:text-base leading-tight" title={displayName}>{displayName}</h3>
          <p
            className="text-slate-400 text-xs truncate transition-opacity duration-300 leading-tight"
            title={subtituloTooltip || subtitulo}
          >
            {/* Renderizamos el subtítulo con los puntos separadores · en teal brillante
                para resaltar la rotación de la marquesina. Los segmentos de texto siguen en slate-400. */}
            {subtitulo.split(' · ').map((segmento, idx, arr) => (
              <React.Fragment key={idx}>
                <span>{segmento}</span>
                {idx < arr.length - 1 && (
                  <span className="text-teal-400 font-bold mx-1.5">·</span>
                )}
              </React.Fragment>
            ))}
          </p>
        </div>
      </div>
      
      {/* Controles — ocupan el resto del espacio.
          - Mobile: wrapper externo `justify-between` separa el grupo de iconos (izquierda)
            de la X (derecha) en el mismo renglón.
          - Desktop (md+): el wrapper de iconos pasa a `display: contents` (md:contents),
            efectivamente desaparece de la jerarquía de layout, y la X queda como el
            último ítem del flex `md:justify-end` — sin filas extra raras.
          flex-wrap del padre se encarga del salto natural en caso de overflow. */}
      <div className="flex items-center gap-1 md:gap-2 flex-wrap min-w-0 flex-1 justify-between md:justify-end">
        {/* Grupo de iconos (todos excepto la X) — alineados a la izquierda en mobile;
            en desktop son hijos directos del flex padre vía display:contents. */}
        <div className="flex items-center gap-1 md:gap-2 flex-wrap md:contents">
        {/* Navegacion */}
        {numPages > 1 && (
          <div className="hidden md:flex items-center gap-1 bg-slate-700 rounded-lg px-2 py-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navegarAPagina(pageNumber - 1)} 
              disabled={pageNumber <= 1}
              className="h-8 w-8 p-0 text-slate-300 hover:text-white hover:bg-slate-600"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <input
              type="number"
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value)}
              onBlur={commitPageInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitPageInput();
                  e.target.blur();
                }
              }}
              className="pdf-page-input w-14 text-center text-sm bg-slate-600 border-0 rounded text-white"
              min={1}
              max={numPages}
            />
            <span className="text-slate-400 text-sm px-1">/ {numPages}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navegarAPagina(pageNumber + 1)} 
              disabled={pageNumber >= numPages}
              className="h-8 w-8 p-0 text-slate-300 hover:text-white hover:bg-slate-600"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Zoom */}
        <div className="hidden md:flex items-center gap-1 bg-slate-700 rounded-lg px-2 py-1">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleZoomOut} 
            disabled={scale <= 0.5}
            className="h-8 w-8 p-0 text-slate-300 hover:text-white hover:bg-slate-600"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <button 
            onClick={handleZoomReset}
            className="text-sm w-14 text-center text-slate-300 hover:text-white"
          >
            {Math.round(scale * 100)}%
          </button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleZoomIn} 
            disabled={scale >= 3.0}
            className="h-8 w-8 p-0 text-slate-300 hover:text-white hover:bg-slate-600"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        {/* Fullscreen toggle — REMOVIDO (15-feb-2026): el visor siempre abre fullscreen por defecto.
            La rama no-fullscreen del visor queda como dead code controlado, no se renderiza. */}

        {/* Lectura Continua */}
        {setContinuousMode && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setContinuousMode(!continuousMode)}
            className={`h-8 px-2 md:px-3 ${continuousMode ? 'text-teal-400 bg-teal-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
            title={continuousMode ? 'Modo pagina individual' : 'Lectura continua'}
            data-testid="continuous-mode-toggle"
          >
            <BookOpen className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{continuousMode ? 'Pagina' : 'Continua'}</span>
          </Button>
        )}

        {/* Buscar */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowSearchPanel(!showSearchPanel)}
          className={`h-8 px-2 md:px-3 ${showSearchPanel ? 'text-teal-400 bg-teal-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
        >
          <Search className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Buscar</span>
        </Button>

        {/* Miniaturas — oculto en mobile (redundante con lectura continua + ocupa mucho en pantalla chica) */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => { setShowThumbnails(!showThumbnails); setShowTableOfContents(false); }}
          className={`hidden md:inline-flex h-8 px-2 md:px-3 ${showThumbnails ? 'text-teal-400 bg-teal-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
          title="Miniaturas de paginas"
          data-testid="thumbnails-toggle-btn"
        >
          <LayoutGrid className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Miniaturas</span>
        </Button>

        {/* Indice / TOC */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => { setShowTableOfContents(!showTableOfContents); setShowThumbnails(false); }}
          className={`h-8 px-2 md:px-3 ${showTableOfContents ? 'text-teal-400 bg-teal-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
          title={tableOfContents.length > 0 
            ? (tableOfContents[0]?.isGenerated ? 'Navegacion rapida' : `Indice (${tableOfContents.length} secciones)`) 
            : 'Indice'}
        >
          <List className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">
            {tocLoading ? 'Cargando...' : 'Indice'}
          </span>
        </Button>

        {/* Agregar marcador en la página actual — REMOVIDO de toolbar, ahora exclusivamente vía FAB flotante (decisión de UX del usuario, 15-feb-2026) */}

        {/* Panel de marcadores — solo icono para mantener toolbar en un solo renglón */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowBookmarksPanel(!showBookmarksPanel)}
          className={`h-8 w-8 p-0 relative ${showBookmarksPanel ? 'text-amber-400 bg-amber-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
          title={marcadoresCount > 0 ? `Marcadores (${marcadoresCount})` : 'Marcadores'}
          data-testid="bookmarks-toggle-btn"
        >
          <Bookmark className="h-4 w-4" />
          {marcadoresCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-[10px] text-white font-bold flex items-center justify-center">
              {marcadoresCount > 99 ? '99+' : marcadoresCount}
            </span>
          )}
        </Button>

        {/* Modo Nocturno */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setNightMode(!nightMode)}
          className={`h-8 w-8 p-0 ${nightMode ? 'text-amber-400 bg-amber-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
          title={nightMode ? 'Modo normal (N)' : 'Modo nocturno (N)'}
        >
          {nightMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* Rotar — útil en mobile cuando el PDF viene apaisado y el usuario no quiere
            salir del visor a desactivar la rotación del teléfono. */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleRotate}
          className={`h-8 w-8 p-0 ${rotation !== 0 ? 'text-teal-400 bg-teal-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
          title={`Rotar pagina (${rotation}°)`}
          data-testid="rotate-page-btn"
        >
          <RotateCw className="h-4 w-4" />
        </Button>

        {/* Atajos — oculto en mobile (no hay teclado físico) */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
          className={`hidden md:inline-flex h-8 w-8 p-0 ${showKeyboardHelp ? 'text-teal-400 bg-teal-500/20' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
          title="Atajos de teclado (?)"
        >
          <Keyboard className="h-4 w-4" />
        </Button>

        {/* Imprimir */}
        {canPrint && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowPrintDialog(true)}
            className="h-8 px-2 md:px-3 text-slate-300 hover:text-white hover:bg-slate-700"
          >
            <Printer className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Imprimir</span>
          </Button>
        )}

        {/* Descargar - SOLO Super Admin */}
        {isSuperAdmin && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleDownload}
            className="h-8 px-2 md:px-3 text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Descargar</span>
          </Button>
        )}

        {/* Limpiar cache HD */}
        {isSuperAdmin && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearHDCache}
            disabled={clearingCache}
            className="h-8 px-2 md:px-3 text-red-400 hover:text-red-300 hover:bg-red-500/20"
            title="Limpiar cache de paginas HD (fuerza re-renderizado)"
            data-testid="clear-hd-cache-btn"
          >
            <Trash2 className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{clearingCache ? 'Limpiando...' : 'Cache HD'}</span>
          </Button>
        )}

        {/* Renderizar (HD + TOC + OCR) — al lado de Cache HD, con badge de progreso */}
        {isSuperAdmin && documentId && (
          <div className="ml-1" data-testid="viewer-precache-wrapper">
            <PrecacheButtonWithBadge
              manualId={documentId}
              docName=""
              fileSizeBytes={pdfSizeBytes}
              variant="viewer"
              alreadyRendered={alreadyRendered}
            />
          </div>
        )}
        </div>
        {/* /Grupo de iconos */}

        {/* Cerrar — alineado a la derecha */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onClose}
          className="h-8 w-8 p-0 text-slate-300 hover:text-red-400 hover:bg-red-500/20"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default PDFToolbar;
